import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Search,
  BookOpen,
  FileText,
  HelpCircle,
  Package,
  Shield,
  Phone,
  Building2,
  Edit3,
  Eye,
  Copy,
  Check,
  Sparkles,
  Save,
  Plus,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Tag,
  Maximize2,
  Minimize2,
  RefreshCw,
  Trash2,
  Layers,
  ArrowRight,
  Sliders,
  CheckCircle2,
  AlertCircle,
  Download,
  Info
} from 'lucide-react';

export type KnowledgeCategory = 'all' | 'identity' | 'products' | 'operations' | 'faq' | 'contact' | 'general';

export interface ParsedSection {
  id: string;
  rawHeader: string;
  title: string;
  category: KnowledgeCategory;
  content: string;
  charCount: number;
  wordCount: number;
}

export interface ExtractedFAQ {
  id: string;
  question: string;
  answer: string;
  sectionTitle: string;
}

interface KnowledgeExplorerModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeText: string;
  onSave: (updatedText: string) => void;
  fileName?: string;
  fileSize?: number;
  lastTrainedAt?: string;
  autoKeywords?: string;
}

// ── Category Definitions & Metadata ──
const CATEGORIES: { id: KnowledgeCategory; label: string; icon: React.ElementType; color: string; bg: string; border: string }[] = [
  { id: 'all', label: 'All Sections', icon: Layers, color: 'text-slate-200', bg: 'bg-slate-800/60', border: 'border-slate-700' },
  { id: 'identity', label: 'Who We Are & Purpose', icon: Building2, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  { id: 'products', label: 'Products, Pricing & SKUs', icon: Package, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  { id: 'operations', label: 'Operations & Policies', icon: Shield, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { id: 'faq', label: 'FAQs & Q&A Matrix', icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  { id: 'contact', label: 'Contact & Escalations', icon: Phone, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  { id: 'general', label: 'Technical Notes', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
];

function inferCategory(title: string, content: string): KnowledgeCategory {
  const combined = `${title} ${content}`.toLowerCase();
  
  if (/(who we are|about us|company|mission|vision|story|background|purpose|overview|philosophy|leadership|team|values|heritage)/i.test(title)) {
    return 'identity';
  }
  if (/(faq|frequently asked|questions|q&a|inquir|common question)/i.test(title)) {
    return 'faq';
  }
  if (/(product|service|pricing|price|catalog|plan|tier|sku|package|cost|fee|feature|spec|menu|rate|subscription)/i.test(title)) {
    return 'products';
  }
  if (/(contact|phone|email|location|branch|address|support|escalat|hotline|hours|reach us|map|direction)/i.test(title)) {
    return 'contact';
  }
  if (/(policy|policies|operation|guideline|terms|warranty|return|refund|shipping|delivery|condition|procedure|cancellation|rule)/i.test(title)) {
    return 'operations';
  }
  
  // Content fallback checks
  if (/(who we are|about our company|our mission|established in)/i.test(combined)) return 'identity';
  if (/(price:|lkr|\$|usd|per month|package include|available in)/i.test(combined)) return 'products';
  if (/(q:\s|question:\s|\*\*q:\*\*)/i.test(combined)) return 'faq';
  if (/(tel:|phone:|email:|hotline:|visit us at)/i.test(combined)) return 'contact';
  if (/(warranty|return policy|terms and condition|operating hour)/i.test(combined)) return 'operations';

  return 'general';
}

function parseMarkdownSections(markdown: string): ParsedSection[] {
  if (!markdown || !markdown.trim()) return [];

  // Match Markdown headers (#, ##, ###, ####) or prominent bold section blocks
  const headerRegex = /(?:^|\n)(#{1,4}\s+[^\n]+|---+\s*\[[^\]]+\]\s*---+)/g;
  const sections: ParsedSection[] = [];
  
  const matches: { index: number; fullHeader: string; title: string }[] = [];
  let match: RegExpExecArray | null;

  while ((match = headerRegex.exec(markdown)) !== null) {
    const rawHeader = match[1].trim();
    // Clean up title: remove ###, numbers, and dashes
    const cleanTitle = rawHeader
      .replace(/^#{1,4}\s*/, '')
      .replace(/^---+\s*\[?/, '')
      .replace(/\]?\s*---+$/, '')
      .replace(/^\d+[\.\)]\s*/, '')
      .trim();

    matches.push({
      index: match.index,
      fullHeader: rawHeader,
      title: cleanTitle || 'Section'
    });
  }

  if (matches.length === 0) {
    // Single un-sectioned document
    const trimmed = markdown.trim();
    return [{
      id: 'section-0',
      rawHeader: '### Synthesized Knowledge Base',
      title: 'Synthesized Knowledge Base',
      category: inferCategory('Synthesized Knowledge Base', trimmed),
      content: trimmed,
      charCount: trimmed.length,
      wordCount: trimmed.split(/\s+/).filter(Boolean).length
    }];
  }

  // Extract content between headers
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const next = matches[i + 1];
    const startIndex = current.index + current.fullHeader.length;
    const endIndex = next ? next.index : markdown.length;
    const content = markdown.slice(startIndex, endIndex).trim();

    sections.push({
      id: `section-${i}`,
      rawHeader: current.fullHeader,
      title: current.title,
      category: inferCategory(current.title, content),
      content,
      charCount: content.length,
      wordCount: content.split(/\s+/).filter(Boolean).length
    });
  }

  return sections;
}

function extractAllFAQs(sections: ParsedSection[], fullMarkdown: string): ExtractedFAQ[] {
  const faqs: ExtractedFAQ[] = [];
  let count = 0;

  // 1. Extract from all sections
  sections.forEach((sec) => {
    const lines = sec.content.split('\n');
    let currentQ = '';
    let currentA = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Question line detection: Q:, **Q:**, ### Q:, or question ending with ?
      const isQuestionLine =
        /^(\-|\*|\d+[\.\)])?\s*(\*\*|__)?(Q|Question)\s*(\:|\.|\-)/i.test(line) ||
        (/^(\-|\*|\d+[\.\)])?\s*(\*\*|__)[^\*\n\?]{4,150}\?(\*\*|__)/.test(line)) ||
        (line.endsWith('?') && line.length < 150 && !line.startsWith('http'));

      if (isQuestionLine) {
        if (currentQ && currentA) {
          faqs.push({
            id: `faq-${count++}`,
            question: currentQ,
            answer: currentA.trim(),
            sectionTitle: sec.title
          });
          currentA = '';
        }
        currentQ = line
          .replace(/^(\-|\*|\d+[\.\)])?\s*/, '')
          .replace(/^(\*\*|__)?(Q|Question)\s*(\:|\.|\-)?\s*/i, '')
          .replace(/^(\*\*|__)/, '')
          .replace(/(\*\*|__)$/, '')
          .trim();
      } else if (currentQ) {
        // Answer line detection
        const cleanAnswerLine = line
          .replace(/^(\-|\*|\d+[\.\)])?\s*/, '')
          .replace(/^(\*\*|__)?(A|Answer)\s*(\:|\.|\-)?\s*/i, '')
          .replace(/^(\*\*|__)/, '')
          .replace(/(\*\*|__)$/, '')
          .trim();

        if (cleanAnswerLine) {
          currentA += (currentA ? '\n' : '') + cleanAnswerLine;
        }
      }
    }

    if (currentQ && currentA) {
      faqs.push({
        id: `faq-${count++}`,
        question: currentQ,
        answer: currentA.trim(),
        sectionTitle: sec.title
      });
    }
  });

  return faqs;
}

export const KnowledgeExplorerModal: React.FC<KnowledgeExplorerModalProps> = ({
  isOpen,
  onClose,
  knowledgeText,
  onSave,
  fileName,
  fileSize,
  lastTrainedAt,
  autoKeywords,
}) => {
  const [activeTab, setActiveTab] = useState<'explorer' | 'faqs' | 'raw_editor' | 'split'>('explorer');
  const [selectedCategory, setSelectedCategory] = useState<KnowledgeCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [draftText, setDraftText] = useState(knowledgeText);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionDraftContent, setSectionDraftContent] = useState('');
  
  // Dynamic Custom Section Creator
  const [isAddingSection, setIsAddingSection] = useState<boolean>(false);
  const [newSectionTitle, setNewSectionTitle] = useState<string>('');
  const [newSectionContent, setNewSectionContent] = useState<string>('');
  
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Sync draft when modal opens
  useEffect(() => {
    if (isOpen) {
      setDraftText(knowledgeText);
      setHasChanges(false);
      setEditingSectionId(null);
      // Auto-expand all sections
      const parsed = parseMarkdownSections(knowledgeText);
      const expandMap: Record<string, boolean> = {};
      parsed.forEach((s) => { expandMap[s.id] = true; });
      setExpandedSections(expandMap);
    }
  }, [isOpen, knowledgeText]);

  // Keyboard shortcut: Esc to close, Ctrl+S to save, Ctrl+F to search
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingSectionId) {
          setEditingSectionId(null);
        } else {
          onClose();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (hasChanges) {
          onSave(draftText);
          setHasChanges(false);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, editingSectionId, hasChanges, draftText, onSave, onClose]);

  // Parse markdown into sections
  const sections = useMemo(() => parseMarkdownSections(draftText), [draftText]);
  const allFAQs = useMemo(() => extractAllFAQs(sections, draftText), [sections, draftText]);

  // Filter sections by category and search
  const filteredSections = useMemo(() => {
    return sections.filter((s) => {
      const matchesCategory = selectedCategory === 'all' || s.category === selectedCategory;
      const matchesSearch = !searchQuery.trim() ||
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.content.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [sections, selectedCategory, searchQuery]);

  // Filter FAQs by search
  const filteredFAQs = useMemo(() => {
    return allFAQs.filter((faq) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.sectionTitle.toLowerCase().includes(query)
      );
    });
  }, [allFAQs, searchQuery]);

  // Stats calculation
  const totalWords = useMemo(() => draftText.trim().split(/\s+/).filter(Boolean).length, [draftText]);
  const totalChars = draftText.length;

  const handleToggleExpand = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExpandAll = (expand: boolean) => {
    const next: Record<string, boolean> = {};
    sections.forEach((s) => { next[s.id] = expand; });
    setExpandedSections(next);
  };

  const handleStartEditSection = (section: ParsedSection) => {
    setEditingSectionId(section.id);
    setSectionDraftContent(section.content);
  };

  const handleSaveSection = (sectionId: string) => {
    const updatedSections = sections.map((sec) => {
      if (sec.id === sectionId) {
        return { ...sec, content: sectionDraftContent };
      }
      return sec;
    });

    // Reconstruct full markdown
    const fullRebuilt = updatedSections
      .map((s) => `${s.rawHeader}\n${s.content}`)
      .join('\n\n');

    setDraftText(fullRebuilt);
    setHasChanges(true);
    setEditingSectionId(null);
  };

  const handleSaveAll = () => {
    onSave(draftText);
    setHasChanges(false);
  };

  const handleCopyMarkdown = () => {
    navigator.clipboard.writeText(draftText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAddCustomSection = (title: string, content: string) => {
    const cleanTitle = title.trim() || 'New Topic & Knowledge Section';
    const cleanContent = content.trim() || '- Add business rules, pricing, or details here...';
    const newSectionBlock = `\n\n### ${cleanTitle}\n${cleanContent}\n`;
    const nextText = `${draftText.trim()}${newSectionBlock}`;
    setDraftText(nextText);
    setHasChanges(true);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 sm:p-6 md:p-8 animate-in fade-in duration-200">
      <div className="relative w-full h-full max-w-7xl bg-slate-950/95 border border-emerald-500/30 rounded-3xl shadow-[0_0_80px_rgba(16,185,129,0.25)] flex flex-col overflow-hidden text-slate-100 font-sans">
        
        {/* ── Top Header Bar ── */}
        <div className="bg-gradient-to-r from-emerald-950/90 via-slate-950 to-teal-950/90 px-6 py-4 border-b border-emerald-500/20 flex flex-wrap items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 shadow-[0_0_20px_rgba(16,185,129,0.4)]">
              <BookOpen size={22} className="animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg font-black tracking-wide text-slate-100 flex items-center gap-2">
                  <span>Luna Knowledge Vault Explorer</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 uppercase tracking-widest">
                    RAG READY
                  </span>
                </h2>
              </div>
              <p className="text-xs text-slate-400 font-mono flex items-center gap-2 mt-0.5">
                <span>{sections.length} Categorized Sections</span>
                <span>•</span>
                <span>{allFAQs.length} Extracted FAQs</span>
                <span>•</span>
                <span>{totalWords.toLocaleString()} Words ({totalChars.toLocaleString()} chars)</span>
              </p>
            </div>
          </div>

          {/* Ingested Document Metadata Pill */}
          {fileName && (
            <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900/90 border border-emerald-500/20 text-xs font-mono">
              <FileText size={14} className="text-emerald-400 shrink-0" />
              <span className="text-slate-200 font-bold truncate max-w-[200px]">{fileName}</span>
              {fileSize && (
                <span className="text-slate-500 text-[10px]">
                  ({(fileSize / 1024).toFixed(1)} KB)
                </span>
              )}
            </div>
          )}

          {/* Header Action Controls */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveAll}
              className={`px-4 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-lg cursor-pointer ${
                hasChanges
                  ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-emerald-500/30 animate-pulse'
                  : 'bg-slate-800/80 text-slate-500 border border-white/5'
              }`}
            >
              <Save size={14} />
              <span>Save to Flow</span>
            </button>

            <button
              type="button"
              onClick={handleCopyMarkdown}
              className="px-3 py-1.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-white/10 text-xs text-slate-300 font-medium flex items-center gap-1.5 transition cursor-pointer"
              title="Copy All Raw Markdown"
            >
              {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
              <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-900/90 hover:bg-rose-500/20 border border-white/10 hover:border-rose-500/40 text-slate-400 hover:text-rose-300 transition cursor-pointer"
              title="Close (Esc)"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Subheader Controls (Search + View Tabs + Category Pills) ── */}
        <div className="px-6 py-3 bg-slate-950/80 border-b border-white/5 flex flex-wrap items-center justify-between gap-3 shrink-0">
          
          {/* Real-time Search Input */}
          <div className="relative flex-1 min-w-[260px] max-w-md">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search topics, questions, pricing, keywords... (Ctrl+F)"
              className="w-full bg-slate-900/90 border border-white/10 rounded-xl pl-9 pr-8 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition font-sans"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* View Mode Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-900/90 border border-white/10 text-xs">
            <button
              type="button"
              onClick={() => setActiveTab('explorer')}
              className={`px-3 py-1 rounded-lg font-bold transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'explorer'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Layers size={13} />
              <span>Structured Explorer</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('faqs')}
              className={`px-3 py-1 rounded-lg font-bold transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'faqs'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <HelpCircle size={13} />
              <span>FAQ Matrix ({allFAQs.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('raw_editor')}
              className={`px-3 py-1 rounded-lg font-bold transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === 'raw_editor'
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Edit3 size={13} />
              <span>Markdown Editor</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('split')}
              className={`hidden md:flex px-3 py-1 rounded-lg font-bold transition items-center gap-1.5 cursor-pointer ${
                activeTab === 'split'
                  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Sliders size={13} />
              <span>Split View</span>
            </button>
          </div>
        </div>

        {/* ── Category Filter Pills (Only shown in Explorer Mode) ── */}
        {activeTab === 'explorer' && (
          <div className="px-6 py-2.5 bg-slate-950/60 border-b border-white/5 flex items-center justify-between gap-2 overflow-x-auto scrollbar-none shrink-0">
            <div className="flex items-center gap-1.5">
              {CATEGORIES.map((cat) => {
                const IconComponent = cat.icon;
                const count = cat.id === 'all'
                  ? sections.length
                  : sections.filter((s) => s.category === cat.id).length;
                const isSelected = selectedCategory === cat.id;

                if (count === 0 && cat.id !== 'all') return null;

                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-1 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 border ${
                      isSelected
                        ? `${cat.bg} ${cat.color} ${cat.border} ring-1 ring-emerald-400/20`
                        : 'bg-slate-900/60 border-white/5 text-slate-400 hover:text-slate-200 hover:bg-slate-900'
                    }`}
                  >
                    <IconComponent size={13} className={isSelected ? cat.color : 'text-slate-400'} />
                    <span>{cat.label}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.2 rounded-full bg-slate-950/80 text-slate-300">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => handleExpandAll(true)}
                className="text-[11px] text-slate-400 hover:text-emerald-300 font-mono transition"
              >
                Expand All
              </button>
              <span className="text-slate-700">|</span>
              <button
                type="button"
                onClick={() => handleExpandAll(false)}
                className="text-[11px] text-slate-400 hover:text-emerald-300 font-mono transition"
              >
                Collapse All
              </button>
            </div>
          </div>
        )}

        {/* ── Main Modal Content Body ── */}
        <div className="flex-1 overflow-hidden relative">

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* 1. STRUCTURED EXPLORER VIEW                                     */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'explorer' && (
            <div className="h-full overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-slate-800">
              {filteredSections.length === 0 ? (
                <div className="text-center py-16 space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-white/10 flex items-center justify-center mx-auto text-slate-500">
                    <Search size={22} />
                  </div>
                  <h3 className="text-sm font-bold text-slate-300">No matching sections found</h3>
                  <p className="text-xs text-slate-500 font-mono">
                    Try modifying your search query or select another category filter.
                  </p>
                </div>
              ) : (
                filteredSections.map((section) => {
                  const isExpanded = expandedSections[section.id] ?? true;
                  const isEditing = editingSectionId === section.id;
                  const catMeta = CATEGORIES.find((c) => c.id === section.category) || CATEGORIES[1];
                  const IconComponent = catMeta.icon;

                  return (
                    <div
                      key={section.id}
                      className={`rounded-2xl border transition-all overflow-hidden ${
                        isExpanded
                          ? `${catMeta.border} bg-slate-900/60 shadow-[0_4px_24px_rgba(0,0,0,0.4)]`
                          : 'border-white/10 bg-slate-900/30 hover:border-white/20'
                      }`}
                    >
                      {/* Section Header Accordion Trigger */}
                      <div
                        onClick={() => handleToggleExpand(section.id)}
                        className="px-5 py-3.5 flex items-center justify-between gap-3 cursor-pointer select-none bg-slate-900/80 hover:bg-slate-900 transition"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded-xl ${catMeta.bg} ${catMeta.color} border ${catMeta.border}`}>
                            <IconComponent size={16} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-xs sm:text-sm font-black text-slate-100 tracking-wide truncate">
                                {section.title}
                              </h3>
                              <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${catMeta.bg} ${catMeta.color} ${catMeta.border}`}>
                                {catMeta.label.split('&')[0].trim()}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-500 font-mono mt-0.5">
                              {section.wordCount} words • {section.charCount} characters
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditSection(section);
                            }}
                            className="p-1.5 rounded-lg bg-slate-800 hover:bg-emerald-500/20 text-slate-400 hover:text-emerald-300 border border-white/5 transition"
                            title="Edit this section"
                          >
                            <Edit3 size={13} />
                          </button>
                          <div className="text-slate-400">
                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          </div>
                        </div>
                      </div>

                      {/* Section Body */}
                      {isExpanded && (
                        <div className="p-5 border-t border-white/5 bg-slate-950/40">
                          {isEditing ? (
                            <div className="space-y-3">
                              <textarea
                                rows={8}
                                value={sectionDraftContent}
                                onChange={(e) => setSectionDraftContent(e.target.value)}
                                className="w-full bg-slate-900 border border-emerald-500/40 rounded-xl p-3 text-slate-100 text-xs font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-emerald-400/40"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingSectionId(null)}
                                  className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSaveSection(section.id)}
                                  className="px-4 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold flex items-center gap-1.5"
                                >
                                  <Save size={13} />
                                  <span>Update Section</span>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-slate-300 font-sans leading-relaxed whitespace-pre-wrap font-mono text-[11px] bg-slate-900/40 p-4 rounded-xl border border-white/5">
                              {section.content}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}

              {/* Dynamic Custom Section Creator */}
              <div className="pt-4 border-t border-white/5">
                  {isAddingSection ? (
                    <div className="p-4 rounded-2xl bg-slate-900/90 border border-emerald-500/30 space-y-3 animate-in fade-in">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-black uppercase tracking-wider text-emerald-300 flex items-center gap-1.5">
                          <Plus size={14} className="text-emerald-400" />
                          <span>Add Custom Knowledge Topic Section</span>
                        </h4>
                        <button
                          type="button"
                          onClick={() => setIsAddingSection(false)}
                          className="text-slate-400 hover:text-slate-200 text-xs p-1"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder="Section Title (e.g. Branch Locations, Admission Rules, Custom Fee Structure...)"
                        value={newSectionTitle}
                        onChange={(e) => setNewSectionTitle(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-emerald-400 font-bold"
                      />
                      <textarea
                        rows={3}
                        placeholder="Enter rules, descriptions, bullet points, or details for this section..."
                        value={newSectionContent}
                        onChange={(e) => setNewSectionContent(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-emerald-400 font-mono text-[11px] resize-none"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setIsAddingSection(false)}
                          className="px-3 py-1.5 rounded-xl text-xs text-slate-400 hover:text-slate-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (newSectionTitle.trim()) {
                              handleAddCustomSection(newSectionTitle, newSectionContent);
                              setNewSectionTitle('');
                              setNewSectionContent('');
                              setIsAddingSection(false);
                            }
                          }}
                          disabled={!newSectionTitle.trim()}
                          className="px-4 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-xs font-black transition cursor-pointer"
                        >
                          + Append Section
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsAddingSection(true)}
                      className="w-full py-2.5 px-4 rounded-xl border border-dashed border-emerald-500/30 hover:border-emerald-400/60 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-300 text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer"
                    >
                      <Plus size={15} />
                      <span>+ Add Custom Knowledge Topic Section</span>
                    </button>
                  )}
                </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* 2. FAQ MATRIX VIEW                                              */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'faqs' && (
            <div className="h-full overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-slate-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <HelpCircle size={18} className="text-amber-400" />
                  <h3 className="text-sm font-black text-slate-200 uppercase tracking-wide">
                    Extracted FAQ Matrix ({filteredFAQs.length})
                  </h3>
                </div>
                <span className="text-xs text-slate-500 font-mono">
                  Autonomous Q&A Matching for WhatsApp Inquiries
                </span>
              </div>

              {filteredFAQs.length === 0 ? (
                <div className="text-center py-16 space-y-3">
                  <HelpCircle size={28} className="mx-auto text-slate-600" />
                  <h3 className="text-sm font-bold text-slate-400">No FAQs matched</h3>
                  <p className="text-xs text-slate-600 font-mono">
                    Ensure questions are formatted with standard Q: and A: blocks.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  {filteredFAQs.map((faq) => (
                    <div
                      key={faq.id}
                      className="p-4 rounded-2xl bg-slate-900/80 border border-amber-500/20 hover:border-amber-500/40 space-y-2.5 transition group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2">
                          <span className="p-1 rounded-md bg-amber-500/20 text-amber-300 text-[10px] font-black font-mono shrink-0">
                            Q
                          </span>
                          <h4 className="text-xs font-bold text-slate-100 leading-snug">
                            {faq.question}
                          </h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(`Q: ${faq.question}\nA: ${faq.answer}`);
                          }}
                          className="text-slate-500 hover:text-amber-300 opacity-0 group-hover:opacity-100 transition p-1"
                          title="Copy Q&A"
                        >
                          <Copy size={12} />
                        </button>
                      </div>

                      <div className="pl-6 text-xs text-slate-300 font-sans leading-relaxed border-l-2 border-amber-500/20">
                        {faq.answer}
                      </div>

                      <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono pt-1">
                        <span>{faq.sectionTitle}</span>
                        <span className="text-emerald-400 flex items-center gap-1">
                          <CheckCircle2 size={10} /> Auto-Answered
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* 3. DIRECT MARKDOWN EDITOR VIEW                                  */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'raw_editor' && (
            <div className="h-full flex flex-col p-6 space-y-3">
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span className="font-mono">Full-Screen Markdown Knowledge Buffer</span>
                <span className="font-mono text-emerald-400">
                  {totalWords} words • {totalChars} chars
                </span>
              </div>

              <textarea
                value={draftText}
                onChange={(e) => {
                  setDraftText(e.target.value);
                  setHasChanges(true);
                }}
                className="flex-1 w-full bg-slate-900 border border-white/10 rounded-2xl p-5 text-slate-100 text-xs font-mono leading-relaxed focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/40 resize-none scrollbar-thin scrollbar-thumb-slate-800"
                placeholder="Write or paste your authoritative Markdown knowledge base here..."
              />
            </div>
          )}

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* 4. SPLIT VIEW (Editor Left | Structured Right)                  */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {activeTab === 'split' && (
            <div className="h-full grid grid-cols-2 divide-x divide-white/5">
              <div className="h-full flex flex-col p-6 space-y-2">
                <span className="text-xs font-mono text-slate-400">Raw Editor</span>
                <textarea
                  value={draftText}
                  onChange={(e) => {
                    setDraftText(e.target.value);
                    setHasChanges(true);
                  }}
                  className="flex-1 w-full bg-slate-900/90 border border-white/10 rounded-2xl p-4 text-slate-100 text-xs font-mono leading-relaxed focus:outline-none focus:border-purple-400 resize-none scrollbar-thin scrollbar-thumb-slate-800"
                />
              </div>

              <div className="h-full overflow-y-auto p-6 space-y-3 scrollbar-thin scrollbar-thumb-slate-800">
                <span className="text-xs font-mono text-slate-400 block mb-2">Live Structured Preview</span>
                {sections.map((sec) => (
                  <div key={sec.id} className="p-4 rounded-xl bg-slate-900/60 border border-white/10 space-y-1.5">
                    <h4 className="text-xs font-black text-slate-200">{sec.title}</h4>
                    <p className="text-[11px] text-slate-400 font-mono whitespace-pre-wrap leading-relaxed">
                      {sec.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ── Footer Status Bar ── */}
        <div className="px-6 py-3 bg-slate-950 border-t border-white/5 flex flex-wrap items-center justify-between gap-3 shrink-0 text-xs font-mono">
          <div className="flex items-center gap-3">
            <span className="text-emerald-400 flex items-center gap-1.5 font-bold">
              <Sparkles size={13} /> Luna Autonomous RAG Active
            </span>
            {autoKeywords && (
              <span className="hidden md:flex items-center gap-1 text-slate-400 truncate max-w-md">
                <Tag size={11} className="text-teal-400" />
                <span className="truncate">{autoKeywords}</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 text-slate-500">
            <span>Press Esc to close</span>
            <span>•</span>
            <span>Ctrl+S to save</span>
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default KnowledgeExplorerModal;
